import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Progress, Typography } from 'antd';

import { colorPalette } from '../../../resources/styles/colorPalette';

import { getThemeColorClass } from '../../../utils/helpers/themeHelpers';

import { selectDarkMode } from '../../../userBrowserSettings/store/browserSettings.selectors';

import { ContentOptimizerQueueStatus } from '../../../constants/contentOptimizer';

import { aiContentFields } from '../../pages/contentOptimizer/contentPage/content/briefTab/contentBriefsConstants';

import RankabilityLoaderLogo from './RankabilityLoaderLogo';

import './RankabilityLoader.scss';

const classes = {
    rankabilityLoader: 'rankability-loader',
    percentageIndicator: 'percentage-indicator',
    progressLineWrapper: 'progress-line-wrapper',
    absolute: 'rankability-loader-absolute',
};

const NewRankabilityLoader = ({
    optimizerStatus,
    currentProgress,
    absolute = false,
    className = '',
    aiContentLoadingField = '',
    isOptimizerLoader = true,
    dispatchHandler = null,
}) => {
    const dispatch = useDispatch();
    const darkMode = useSelector(selectDarkMode);

    const [localProgress, setLocalProgress] = useState(Number(currentProgress));

    /**
     * Separate logic for updating loading percentage.
     * It might be used for different loaders.
     * You need to change only `dispatchHandler` and create relevant variable in the redux store.
     */
    useEffect(() => {
        let timeoutId = null;
        if (!isOptimizerLoader) {
            timeoutId = setTimeout(() => {
                const updatedProgress = currentProgress + Math.random() * 4;
                if (dispatchHandler) {
                    dispatch(dispatchHandler(updatedProgress));
                }
                setLocalProgress(updatedProgress);
            }, 750);
        }
        return () => {
            if (timeoutId) {
                clearInterval(timeoutId);
            }
        };
    }, [isOptimizerLoader, currentProgress, localProgress]);

    useEffect(() => {
        let timeoutId = null;

        if (isOptimizerLoader && optimizerStatus !== ContentOptimizerQueueStatus.QUEUED) {
            const updatedProgress =
                localProgress +
                Math.random() * (aiContentLoadingField === aiContentFields.OUTLINE ? 5 : 2);
            let newLocalProgress =
                updatedProgress < 100
                    ? updatedProgress > currentProgress
                        ? localProgress + 0.1
                        : updatedProgress
                    : 100;

            newLocalProgress = parseFloat(newLocalProgress.toFixed(1));
            timeoutId = setTimeout(() => {
                setLocalProgress((prevState) => {
                    if (prevState !== 100) {
                        const newProgress =
                            prevState === newLocalProgress ? prevState + 0.1 : newLocalProgress;
                        if (dispatchHandler && currentProgress < newProgress) {
                            dispatch(dispatchHandler(newProgress));
                        }
                        return newProgress;
                    }
                    return prevState;
                });
            }, 750);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isOptimizerLoader, optimizerStatus, currentProgress, localProgress, aiContentLoadingField]);

    return (
        <div
            className={`${classes.rankabilityLoader} ${
                absolute ? classes.absolute : ''
            } ${className} ${getThemeColorClass(darkMode)}`}
        >
            <RankabilityLoaderLogo optimizerStatus={optimizerStatus} darkMode={darkMode} />
            <div className={classes.progressLineWrapper}>
                <Progress
                    percent={localProgress}
                    showInfo={false}
                    status='active'
                    strokeColor={colorPalette.colorPrimary}
                    size='small'
                />
            </div>

            <Typography
                className={`${classes.percentageIndicator} ${getThemeColorClass(darkMode)}`}
            >
                {optimizerStatus !== ContentOptimizerQueueStatus.QUEUED
                    ? `${localProgress.toFixed(1)}%`
                    : `Queued...`}
            </Typography>
        </div>
    );
};

export default NewRankabilityLoader;
